import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Icon, Tooltip, Tr, Td, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { SlackInstallationGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  GetActiveSlackInstallationsDocumentGQLType,
  useDeactivateSlackInstallationForChannelMutation,
} from './slack.gql.generated';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { FiTrash2 } from 'react-icons/fi';
import {
  toastId,
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

export const SlackInstallationRow = ({
  slackInstallation,
  organizationId,
}: {
  slackInstallation: SlackInstallationGQLType;
  organizationId: string;
}) => {
  const {
    teamId,
    teamName,
    incomingWebhookChannelId,
    incomingWebhookChannelName,
    created_at: createdAt,
  } = slackInstallation;

  const toast = useToast();
  const [
    deactivateSlackInstallationForChannel,
  ] = useDeactivateSlackInstallationForChannelMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [GetActiveSlackInstallationsDocumentGQLType],
  });

  if (!teamId) {
    return <></>;
  }

  const deactivateChannelClickHandler = async () => {
    try {
      const {
        data: deactivateSlackInstallationForChannelData,
      } = await deactivateSlackInstallationForChannel({
        variables: {
          teamId,
          incomingWebhookChannelId,
          organizationId,
        },
      });

      if (
        deactivateSlackInstallationForChannelData &&
        deactivateSlackInstallationForChannelData.deactivateSlackInstallationForChannel &&
        !toast.isActive(toastId)
      ) {
        const response =
          deactivateSlackInstallationForChannelData
            .deactivateSlackInstallationForChannel.response;
        successToastConfig.title = response;
        toast(successToastConfig);
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title =
          'Unable to deactivate this Slack channel. Please try again.';
        toast(errorToastConfig);
      }
      throw err;
    }
  };

  return (
    <Tr className="slack-installation-row">
      <Td whiteSpace="pre-wrap">
        {`${teamName} `}
        <Tooltip
          key={`key-${Math.random()}`}
          placement="top"
          label={`Team ID: ${teamId}`}
        >
          <InfoOutlineIcon />
        </Tooltip>
      </Td>
      <Td whiteSpace="pre-wrap">
        {`${incomingWebhookChannelName} `}
        <Tooltip
          key={`key-${Math.random()}`}
          placement="top"
          label={`Channel ID: ${incomingWebhookChannelId}`}
        >
          <InfoOutlineIcon />
        </Tooltip>
      </Td>
      <Td>{commonDate.epochToLocaleDateString(createdAt)}</Td>
      <Td>
        <Tooltip label="Deactivate" placement="top">
          <span>
            <Icon
              cursor="pointer"
              onClick={async () => {
                Sentry.addBreadcrumb({
                  category: 'ui.click',
                  message: `Clicked deactivate Slack channel`,
                  level: Sentry.Severity.Info,
                });
                await deactivateChannelClickHandler();
              }}
              as={FiTrash2}
            />
          </span>
        </Tooltip>
      </Td>
    </Tr>
  );
};
